import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { AUDIENCES, type AudienceId } from '@shared/constants/audiences';

/**
 * Source de vérité UNIQUE pour le profil audience côté client.
 *
 * - Persisté dans `localStorage` (clé `sef_audience_store`).
 * - Miroir dans le cookie `sef_audience` (90 jours) pour que le middleware Express
 *   puisse résoudre l'audience à la prochaine requête SSR.
 * - Hydratation depuis le cookie au mount si le localStorage est vide
 *   (utile en cas de purge ITP Safari ou navigation multi-onglets).
 *
 * Toute modification de l'audience DOIT passer par ce store
 * (`setAudience`, `clearAudience`). Ne plus écrire directement dans
 * `document.cookie` ou `localStorage` côté composants.
 */

export const AUDIENCE_COOKIE = 'sef_audience';
const AUDIENCE_COOKIE_DAYS = 90;

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function expireCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match || !match[1]) return null;
  return decodeURIComponent(match[1]);
}

function isValidAudienceId(value: string | null | undefined): value is AudienceId {
  return !!value && AUDIENCES.some((a) => a.id === value);
}

interface AudienceState {
  /** ID d'audience actif (null = aucun profil choisi). */
  audienceId: AudienceId | null;
  /** La modale d'onboarding a-t-elle déjà été vue (sélection OU fermeture explicite) ? */
  modalSeen: boolean;
  /** Le store a-t-il fini son hydratation depuis localStorage/cookie ? */
  hydrated: boolean;
  /**
   * Profil verrouillé par une URL /[audience]/[outil].
   * Non persisté — réinitialisé à chaque navigation.
   * Priorité maximale dans useEffectiveAudience (> URL > store).
   */
  lockedAudienceId: AudienceId | null;

  /** Sélectionne un profil et synchronise le cookie. Marque automatiquement la modale comme vue. */
  setAudience: (id: AudienceId) => void;
  /** Efface le profil + cookie. La modale reste marquée comme vue (UX). */
  clearAudience: () => void;
  /** Marque la modale comme vue (sans choisir de profil). */
  markModalSeen: () => void;
  /** Appelé une fois après l'hydratation (côté client). Aligne le cookie sur le state. */
  hydrate: () => void;
  /** Verrouille le profil pour la durée d'une page /[audience]/[outil]. */
  setLockedAudience: (id: AudienceId) => void;
  /** Déverrouille le profil (appelé quand on quitte la page outil). */
  clearLock: () => void;
}

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

export const useAudienceStore = create<AudienceState>()(
  persist(
    (set, get) => ({
      audienceId: null,
      modalSeen: false,
      hydrated: false,
      lockedAudienceId: null,

      setAudience: (id) => {
        set({ audienceId: id, modalSeen: true });
        setCookie(AUDIENCE_COOKIE, id, AUDIENCE_COOKIE_DAYS);
      },

      clearAudience: () => {
        set({ audienceId: null });
        expireCookie(AUDIENCE_COOKIE);
      },

      markModalSeen: () => set({ modalSeen: true }),

      hydrate: () => {
        if (get().hydrated) return;
        const stateId = get().audienceId;
        if (stateId) {
          setCookie(AUDIENCE_COOKIE, stateId, AUDIENCE_COOKIE_DAYS);
        } else {
          const fromCookie = readCookie(AUDIENCE_COOKIE);
          if (isValidAudienceId(fromCookie)) {
            set({ audienceId: fromCookie, modalSeen: true });
          }
        }
        set({ hydrated: true });
      },

      setLockedAudience: (id) => {
        set({ lockedAudienceId: id, audienceId: id, modalSeen: true });
        setCookie(AUDIENCE_COOKIE, id, AUDIENCE_COOKIE_DAYS);
      },

      clearLock: () => set({ lockedAudienceId: null }),
    }),
    {
      name: 'sef_audience_store',
      storage: createJSONStorage(() =>
        typeof window === 'undefined' ? noopStorage : window.localStorage,
      ),
      partialize: (state) => ({
        audienceId: state.audienceId,
        modalSeen: state.modalSeen,
      }),
      version: 1,
      // Hydrate cookie/banner side-effects dès que le store sort de localStorage.
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrate();
      },
    },
  ),
);

/**
 * Vérifie si un ID est valide. Exporté pour les hooks/composants qui lisent
 * des sources externes (URL, props, params).
 */
export function isAudienceId(value: unknown): value is AudienceId {
  return typeof value === 'string' && isValidAudienceId(value);
}
